import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import CommandLogger from "./components/CommandLogger/CommandLogger";
import ReportWriter from "./components/ReportWriter/ReportWriter";
import style from "./pentestWindows.module.css";
import { PentestContext, WindowType } from "../../contexts/PentestContext";
import { AppContext } from "../../contexts/AppContext";
import { apiGenerateSingleVulnerability, apiPentestsUpdate, apiVulnerabilitiesDelete, apiVulnerabilitiesRetrieve } from "../../sdk";
import MetadataModal from "./components/ReportWriter/components/MetadataModal/MetadataModal";
import { AuthContext } from "../../contexts/AuthContext";
import { fetchPentestStatus } from "./fetchPentest";
import axios from "axios";
import Sidebar from "./components/Sidebar/Sidebar";
import { useSearchParams } from "react-router-dom";
import TemplateEditor from "./components/TemplateEditor/TemplateEditor";
import { ChangePentest, Pentest, Vulnerability } from "../../sdkStructs";
import TeamCollaboration from "./components/TeamCollaboration/TeamCollaboration.tsx";
import VulnModal from "../Common/Modals/VulnModal/VulnModal.tsx";

function getWindowFromURL(param: string | null) {
  if (param === "logger" || param === "editor" || param === "template" || param === "collaboration") return param;

  // No window specified in URL, set default option
  history.pushState(null, "", `?window=logger`);
  return "logger";
}

function PentestWindows() {
  const { setLoading, pushAlert, setShowModal } = useContext(AppContext);
  const token = useContext(AuthContext).token;

  const [searchParams, setSearchParams] = useSearchParams({ window: "logger" });
  const currentWindow: WindowType = getWindowFromURL(searchParams.get("window"));

  const [metadataModal, setMetadataModal] = useState<boolean>(false);

  const storedPentestId = Number(sessionStorage.getItem("pentestId"));
  const [pentestId, setPentestId] = useState(storedPentestId || -1);
  const [pentest, setPentest] = useState<Pentest>();
  const [pentestVulns, setPentestVulns] = useState<Vulnerability[]>([]);

  const [generating, setGenerating] = useState<boolean[]>([]);
  const [maximize, setMaximize] = useState(false);
  const vulnRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [showVuln, setShowVuln] = useState<Vulnerability | undefined>(undefined);
  const [showVulnOrderNumber, setShowVulnOrderNumber] = useState<number>(0);

  const scrollToVuln = useCallback((orderId: number) => {
    const ref = vulnRefs.current[orderId];
    if (!ref) return;
    const elementPosition = ref.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - 100; // offset from the top

    window.scrollTo({
      behavior: "smooth",
      top: offsetPosition,
    });
  }, []);

  // Convert pentest sections to editor friendly format
  const refreshVulns = useCallback(async () => {
    try {
      const response = await apiVulnerabilitiesRetrieve(token, pentestId);
      setPentestVulns(response.data);
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        return;
      }
      console.log("ERROR GENERATING SECTIONS:", error.response);
    }
  }, [token, pentestId]);

  useEffect(() => {
    if (token !== "" && pentestId >= 1) refreshVulns();
  }, [token, pentestId, refreshVulns]);

  function openVulnModal(showVuln: Vulnerability, orderNumber: number) {
    setShowVuln(showVuln);
    setShowVulnOrderNumber(orderNumber);
  }
  const closeVulnModal = useCallback((refresh: boolean) => {
    if (refresh) refreshVulns();
    setShowVuln(undefined);
  }, [refreshVulns]);

  const generateSingleVuln = useCallback(
    async (vuln: Vulnerability, id: number) => {
      try {
        await apiGenerateSingleVulnerability(token, vuln.ID);
        refreshVulns();
        pushAlert({
          title: "Generation Success",
          text: "Vulnerability " + id + " was generated succesfully",
          closeButton: true,
          type: "success",
          time: 4000,
        });
      } catch (error) {
        if (!axios.isAxiosError(error)) {
          return;
        }
        console.log("ERROR GENERATING SINGLE VULNERABILITY:", error.response);
        pushAlert({
          title: "Generation Failed",
          text: "Error generating vulnerability " + id + ": " + error.response?.data?.error,
          closeButton: true,
          type: "error",
        });
      }
    },
    [token, refreshVulns, pushAlert],
  );

  const selectRemainingVulnsToGenerate = useCallback(async () => {
    setGenerating((prevGenerating) => {
      const newGenerating = { ...prevGenerating };
      pentestVulns.map((vuln) => {
        if (!vuln.Description) {
          newGenerating[vuln.ID] = true;
        }
      });
      return newGenerating;
    });

    pentestVulns.map(async (vuln, id) => {
      if (!vuln.Description) {
        await generateSingleVuln(vuln, id);
        setGenerating((prevGenerating) => ({
          ...prevGenerating,
          [vuln.ID]: false,
        }));
      }
    });
  }, [pentestVulns, generateSingleVuln]);

  const selectVulnToGenerate = useCallback(
    async (data: Vulnerability, id: number) => {
      setGenerating((prevGenerating) => ({
        ...prevGenerating,
        [data.ID]: true,
      }));

      await generateSingleVuln(data, id);

      setGenerating((prevGenerating) => ({
        ...prevGenerating,
        [data.ID]: false,
      }));
    },
    [generateSingleVuln],
  );

  const removeVuln = useCallback(async (vulnId: number) => {
    setLoading(true);
    try {
      await apiVulnerabilitiesDelete(token, vulnId);
      closeVulnModal(true);
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        return;
      }
      console.log("ERROR REMOVING VULN:", error.response);
    }
    setLoading(false);
  }, [closeVulnModal, setLoading, token]);

  const confirmRemoveVuln = useCallback((vulnId: number) => {
    setShowModal("confirm", {
      text: "Are you sure you want to delete this vulnerability?",
      confirm: async () => {
        removeVuln(vulnId);
      },
      close: () => setShowModal("none", undefined),
    });
  }, [removeVuln, setShowModal]);


  const pentestValue = useMemo(
    () => ({
      pentestId,
      setPentestId,
      pentest,
      setPentest,
      pentestVulns,
      setPentestVulns,
      refreshVulns,
      generating,
      setGenerating,
      selectVulnToGenerate,
      selectRemainingVulnsToGenerate,
      currentWindow,
      setCurrentWindow: (e: WindowType) => {
        setSearchParams((prev) => {
          prev.set("window", e);
          return prev;
        });
      },
      scrollToVuln,
      vulnRefs,
      openVulnModal,
      confirmRemoveVuln
    }),
    [
      pentestId,
      pentest,
      pentestVulns,
      refreshVulns,
      generating,
      selectVulnToGenerate,
      selectRemainingVulnsToGenerate,
      currentWindow,
      setSearchParams,
      scrollToVuln,
      vulnRefs,
      confirmRemoveVuln,
    ],
  );

  useEffect(() => {
    async function load() {
      const response = await fetchPentestStatus(token, pentestId);
      if (response === undefined) return;
      setPentest(response);
    }

    if (token !== "" && pentestId >= 1) load();
  }, [pentestId, token]);

  function getMetadataModal() {
    if (!metadataModal) return;
    if (!pentest) {
      return;
    }
    return (
      <MetadataModal data={pentest} closeModal={() => setMetadataModal(false)} changePentest={savePentestChanges} />
    );
  }

  // Function to save sections to pentest
  async function savePentestChanges(pentestChanges: ChangePentest) {
    setLoading(true);
    try {
      await apiPentestsUpdate(token, pentestId, pentestChanges);
      setPentest(undefined);
      const newPentest = await fetchPentestStatus(token, pentestId);
      if (newPentest) setPentest(newPentest);
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        return;
      }
      console.log("ERROR SAVING PENTEST:", error.response);
    }
    setLoading(false);
  }

  return (
    <PentestContext.Provider value={pentestValue}>
      <div className={style.wrapper}>
        <Sidebar setMetadataModal={setMetadataModal} maximize={maximize} setMaximize={setMaximize} />
        <div className={style.windowWrapper + " " + (maximize ? style.windowWrapperSmall : "")}>
          <CommandLogger />
          <ReportWriter />
            <TemplateEditor />
          <TeamCollaboration />
        </div>
        {getMetadataModal()}
      </div>

      {showVuln !== undefined && (
        <VulnModal
          closeModal={closeVulnModal}
          showVuln={showVuln}
          token={token}
          pentestId={pentestId}
          generateSingleVuln={() => selectVulnToGenerate(showVuln, showVulnOrderNumber)}
          orderNumber={showVulnOrderNumber}
        />
      )}
    </PentestContext.Provider>
  );
}

export default PentestWindows;
